import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from '@ds/core';
import { Modal } from '@ds/feedback';

export interface PedidoDeConfirmacao {
    titulo: string;
    descricao?: ReactNode;
    /** O verbo do botão: "Marcar como perdido", não "OK". */
    confirmar: string;
}

interface PedidoAberto extends PedidoDeConfirmacao {
    responder: (confirmado: boolean) => void;
}

/**
 * Confirmação antes de ação destrutiva.
 *
 * Devolve a função que pergunta (e espera a resposta) e o diálogo, que a tela
 * põe em qualquer lugar do JSX. Marcar lead como perdido, desativar acesso ou
 * remover credencial aconteciam num clique, sem volta e sem pergunta.
 */
export function useConfirmacao(): [(pedido: PedidoDeConfirmacao) => Promise<boolean>, ReactNode] {
    const [aberto, setAberto] = useState<PedidoAberto | null>(null);

    const confirmar = useCallback(
        (pedido: PedidoDeConfirmacao) => new Promise<boolean>((resolver) => {
            setAberto({ ...pedido, responder: resolver });
        }),
        [],
    );

    const responder = (confirmado: boolean) => {
        aberto?.responder(confirmado);
        setAberto(null);
    };

    const dialogo = (
        <Modal
            open={aberto !== null} tone="danger" width={440}
            title={aberto?.titulo} description={aberto?.descricao}
            onClose={() => responder(false)}
            footer={
                <>
                    <Button variant="secondary" onClick={() => responder(false)}>Cancelar</Button>
                    <Button variant="danger" onClick={() => responder(true)}>{aberto?.confirmar}</Button>
                </>
            }
        />
    );

    return [confirmar, dialogo];
}
